/**
 * GameUI - handles all UI interactions and display
 * This module manages the game interface, replacing jQuery with vanilla DOM utilities
 */

import { $dom, windowWidth, windowHeight, createElement } from '../utils/DOM';
import { gameController } from './GameController';
import { Settings } from './Settings';
import { Globals } from './Globals';
import { GameActions } from '../game/GameActions';
import { GameCommands } from '../game/GameCommands';
import { GameTimers } from '../game/GameTimers';
import { GameHistory } from '../game/GameHistory';
import { Finder } from '../utils/Finder';
import { Interactables, mainTextEndsWithTurnSeparator, GetActionCount, actionShouldBeVisible, nameForAction, objectContainsObject, objecttostring, characterHasObject, CharToString, ResetLoopObjects, RestoreLoopObject, SetExits, SetCommandInput, getObjectClass, maybeAppendTurnSeparatorForInput, processTextForOutput, scrollMainTextToTurnStart, escapeHtmlSpecialCharacters, roomDisplayName } from '../utils/Utilities';
import { objectToString } from '../models/Object';
import { Character } from '../models/Character';
import { PerformTextReplacements } from '../utils/TextHandler';
import { ENGINE_LOGGING_ENABLED } from '../utils/Constants';

declare let TheGame: any;
declare function UpdateStatusBars(): void;

// Forward declarations
declare namespace ActionRecorder {
    function choseInputAction(text: string): void;
    function actedOnObject(obj: any, text: string): void;
}
declare var version: string;

// Store body click handler for selection menu cleanup
let bodyClickHandler: ((e: Event) => void) | null = null;
let selectionMenuClickHandler: ((e: MouseEvent) => void) | null = null;

const CANCEL_CHOICE_VALUE = 'cancel';
const PLAYER_PORTRAIT_SELECTORS = ['#PlayerImg', '#PortraitImageLayers'];
const PORTRAIT_CONTAINER_SELECTOR = '.compass-and-portrait';
const PORTRAIT_HOLDER_SELECTOR = '.portrait-holder';
const INVENTORY_PANEL_SELECTOR = '#InventoryPanel';
const PLAYER_ROW_PANELS_SELECTOR = '#player-row-panels-container';

function isCancelChoice(text: string): boolean {
    return text.trim().toLowerCase() === CANCEL_CHOICE_VALUE;
}

function markCancelChoice(menuSelector: string, text: string): void {
    if (isCancelChoice(text)) {
        $dom(menuSelector).attr('data-has-cancel-choice', 'true');
    }
}

function menuHasCancelChoice(menuSelector: string): boolean {
    return $dom(menuSelector).attr('data-has-cancel-choice') === 'true';
}

function clearCancelChoiceMarker(menuSelector: string): void {
    $dom(menuSelector).removeAttr('data-has-cancel-choice');
}

function applyPortraitVisibility(): void {
    const hidePortrait = !!TheGame?.HidePortrait;
    if (hidePortrait) {
        $dom(PORTRAIT_CONTAINER_SELECTOR).addClass('portrait-hidden');
    } else {
        $dom(PORTRAIT_CONTAINER_SELECTOR).removeClass('portrait-hidden');
    }
    $dom(PORTRAIT_HOLDER_SELECTOR).css('display', hidePortrait ? 'none' : '');
    for (const selector of PLAYER_PORTRAIT_SELECTORS) {
        const selectorDom = $dom(selector) as any;
        selectorDom.css('display', hidePortrait ? 'none' : '');
        selectorDom.css('visibility', hidePortrait ? 'hidden' : 'visible');
    }
}

function applyInventoryVisibility(): void {
    const hideInventory = TheGame?.ShowInventory === false;
    if (hideInventory) {
        const inventoryEl = $dom(INVENTORY_PANEL_SELECTOR).get(0);
        if (inventoryEl && inventoryEl.parentElement) {
            inventoryEl.parentElement.removeChild(inventoryEl);
        }
    }
    if (hideInventory) {
        $dom(PLAYER_ROW_PANELS_SELECTOR).addClass('inventory-hidden');
    } else {
        $dom(PLAYER_ROW_PANELS_SELECTOR).removeClass('inventory-hidden');
    }
    $dom(INVENTORY_PANEL_SELECTOR).css('display', hideInventory ? 'none' : '');
}

export const GameUI = {
    saveDisabled: false,
    INDENT_STR: '\u00A0\u00A0',
    darkColorMap: {} as { [key: string]: string | number[] },
    darkColorElement: null as HTMLElement | null,
    bgMusicTimer: -1 as number,

    setGameTitle: function (): void {
        const title = gameController.title();
        let versionText = '';
        if (TheGame && TheGame.GameVersion !== undefined && TheGame.GameVersion.length > 0) {
            versionText = ' (' + TheGame.GameVersion + ')';
        }

        document.title = title + versionText;
        $dom('.game-title').text(title);
        $dom('.game-version').text(versionText);
    },

    setDefaultCompass: function (): void {
        $dom(".compass-rose").css("background-image", 'url("images/300px-Compass_Rose_English_North.png")');
        $dom(".compass-up-down").css("background-image", 'url("images/300px-Compass_Rose_UD.png")');
    },

    setInputMenuTitle: function (act: any): void {
        $dom("#InputMenuTitle").text(PerformTextReplacements(act.CustomChoiceTitle, null));
        $dom("#inputmenu").css("visibility", "visible");
        const hasCancelChoice = menuHasCancelChoice("#inputmenu");
        const cancellable = ((act.EnhInputData && act.EnhInputData.bAllowCancel) || (act.CustomChoices && act.CustomChoices.length === 0))
            && !hasCancelChoice;
        $dom("#inputmenu").toggleClass('cancellable', cancellable);
    },

    showGameElements: function (): void {
        $dom("#RoomThumbImg").css("visibility", "visible");
        $dom("#RoomThumbImg").removeAttr("data-disabled");
        applyPortraitVisibility();
        $dom("#PlayerImg").removeAttr("data-disabled");
        $dom("#RoomObjectsPanel").removeAttr("data-disabled");
        $dom("#RoomObjectsPanel").css("visibility", "visible");
        $dom("#VisibleCharactersPanel").removeAttr("data-disabled");
        $dom("#VisibleCharactersPanel").css("visibility", "visible");
        applyInventoryVisibility();
        if (TheGame?.ShowInventory !== false) {
            $dom("#InventoryPanel").css("visibility", "visible");
        }
        $dom("#InventoryPanel").removeAttr("data-disabled");
        $dom(".compass-direction").removeAttr("data-disabled");
        $dom(".compass-direction").css("visibility", "visible");
        (window as any).SetExits?.();
    },

    hideGameElements: function (): void {
        $dom("#PlayerImg").attr("data-disabled", "true");
        applyPortraitVisibility();
        $dom("#RoomThumbImg").attr("data-disabled", "true");
        $dom("#RoomObjectsPanel").attr("data-disabled", "true");
        $dom("#VisibleCharactersPanel").attr("data-disabled", "true");
        $dom("#InventoryPanel").attr("data-disabled", "true");
        $dom(".compass-direction").attr("data-disabled", "true");
    },

    disableSaveAndLoad: function (): void {
        this.saveDisabled = true;
        $dom('#back').prop('disabled', true)
            .prop('title', 'Cannot go back now');
    },

    enableSaveAndLoad: function (): void {
        this.saveDisabled = false;
        $dom('#back').prop('disabled', !GameHistory.canGoBack())
            .prop('title', GameHistory.noGoBackReason());
    },

    disableMenuButtons: function (): void {
        // Disable menu buttons that should not be used while awaiting input.
        // Do not disable #Continue here; pause flow relies on it remaining clickable.
        $dom('#save').prop('disabled', true);
        $dom('#back').prop('disabled', true);
        $dom('#cheat_button').prop('disabled', true);
        $dom('#options_button').prop('disabled', true);
        $dom('#download_button').prop('disabled', true);
    },

    enableMenuButtons: function (): void {
        // Re-enable menu buttons when input is no longer requested
        $dom('#save').prop('disabled', false);
        $dom('#back').prop('disabled', !GameHistory.canGoBack());
        $dom('#cheat_button').prop('disabled', false);
        $dom('#options_button').prop('disabled', false);
        $dom('#download_button').prop('disabled', false);
    },

    showPauseButton: function (): void {
        const continueBtn = $dom('#Continue').get(0) as HTMLElement | undefined;
        if (continueBtn) continueBtn.removeAttribute('disabled');
    },

    hidePauseButton: function (): void {
        const continueBtn = $dom('#Continue').get(0) as HTMLElement | undefined;
        if (continueBtn) continueBtn.setAttribute('disabled', 'disabled');
    },

    clearInputChoices: function (): void {
        $dom("#inputchoices").empty();
        clearCancelChoiceMarker("#inputmenu");
    },

    clearCmdInputChoices: function (): void {
        $dom("#cmdinputchoices").empty();
        clearCancelChoiceMarker("#cmdinputmenu");
    },

    setCmdInputMenuTitle: function (act: any, title: string): void {
        $dom("#cmdInputMenuTitle").text(title);
        $dom("#cmdinputmenu").css("visibility", "visible");
        const hasCancelChoice = menuHasCancelChoice("#cmdinputmenu");
        const cancellable = Boolean(act?.EnhInputData?.bAllowCancel) && !hasCancelChoice;
        $dom("#cmdinputmenu").toggleClass('cancellable', cancellable);
    },

    addInputChoice: function (act: any, text: string, value: any): void {
        const div = createElement<HTMLDivElement>('div', {
            class: 'inputchoices'
        }, text);
        markCancelChoice("#inputmenu", text);

        div.addEventListener('click', () => {
            const selectedObj = value;
            if (selectedObj != null) {
                maybeAppendTurnSeparatorForInput();
                (window as any).ActionRecorder?.choseInputAction?.(text);
                Globals.additionalData = selectedObj;
                void gameController.stopAwaitingInputAsync();
                $dom("#inputmenu").css("visibility", "hidden");
            }
        });

        $dom("#inputchoices").append(div);
    },

    addCmdInputChoice: function (text: string, value: any): void {
        const div = createElement<HTMLDivElement>('div', {
            class: 'inputchoices'
        }, text);
        markCancelChoice("#cmdinputmenu", text);

        div.addEventListener('click', () => {
            const selectedObj = value;
            if (selectedObj != null) {
                maybeAppendTurnSeparatorForInput();
                $dom("#cmdinputmenu").hide();
                gameController.stopAwaitingInput();
                $dom("#cmdinputmenu").css("visibility", "hidden");
                (window as any).ActionRecorder?.choseInputAction?.(text);
                (window as any).SetCommandInput?.(Globals.variableGettingSet, selectedObj);
                // Resume the suspended command chain (CT_SETVARIABLEBYINPUT awaiter).
                // The chain's caller owns running timer events; avoid double-firing.
                const resumed = gameController.resumeExecution();
                if (!resumed) {
                    // Legacy path: nothing awaiting, run timers ourselves.
                    void gameController.runCommandsAndCheckTimers();
                    GameUI.onInteractionResume();
                }
            }
        });

        $dom("#cmdinputchoices").append(div);
        $dom("#cmdinputmenu").show();
    },

    addCharacterOptions: function (act?: any, withObjects?: boolean): void {
        Interactables.characters().forEach((character) => {
            if (act) {
                GameUI.addInputChoice(act, CharToString(character), character.Charname);
            } else {
                GameUI.addCmdInputChoice(CharToString(character), character.Charname);
            }

            if (withObjects) {
                // Also add objects in the character inventory if shown
                GameUI.addCharacterObjectOptions(act, character);
            }
        });
    },

    addObjectOptions: function (act?: any): void {
        Interactables.roomAndInventoryObjects().forEach((obj) => {
            if (act) {
                GameUI.addInputChoice(act, objecttostring(obj), obj);
            } else {
                GameUI.addCmdInputChoice(objecttostring(obj), obj);
            }
        });
        // Also add objects in characters inventory if shown
        Interactables.characters().forEach((character) => {
            GameUI.addCharacterObjectOptions(act, character);
        });
    },

    addCharacterObjectOptions: function (act: any, character: any): void {
        if (character.bAllowInventoryInteraction) {
            Interactables.characterObjects(character).forEach((obj) => {
                if (act) {
                    this.addInputChoice(act, objectToString(obj), obj);
                } else {
                    this.addCmdInputChoice(objectToString(obj), obj);
                }
            });
        }
    },

    setCmdInputForCustomChoices: function (title: string, tempcommand: any): void {
        this.clearCmdInputChoices();
        if (tempcommand.CustomChoices) {
            for (let i = 0; i < tempcommand.CustomChoices.length; i++) {
                const text = PerformTextReplacements(tempcommand.CustomChoices[i], null);
                this.addCmdInputChoice(text, text);
            }
        }
        this.setCmdInputMenuTitle(tempcommand, title);
    },

    showTextMenuChoice: function (title: string): void {
        $dom("#textMenuTitle").text(title);
        $dom("#textchoice").css("visibility", "visible");
        const input = $dom("#textchoice input").get(0) as HTMLInputElement;
        if (input) input.focus();
    },

     addActionChoice: function (obj: any, action: any, text: string): HTMLElement {
        const div = createElement<HTMLDivElement>('div', {
            class: 'ActionChoices',
            value: action.name
        }, text);

        div.addEventListener('click', async (e) => {
            const selectionchoice = div.getAttribute('value');
            const selectiontext = div.textContent || '';
            if (selectionchoice != null) {
                await gameController.executeAndRunTimersAsync(async () => {
                    (window as any).ActionRecorder?.actedOnObject?.(obj, selectiontext);
                    let logLine;
                    const ragsObjClass = (window as any).ragsobject;
                    const charClass = (window as any).character;
                    const roomClass = (window as any).room;
                    const playerClass = (window as any).player;
                    if (ragsObjClass && obj instanceof ragsObjClass) {
                        logLine = objectToString(obj) + ' - ';
                    } else if (charClass && obj instanceof charClass) {
                        logLine = CharToString(obj) + ' - ';
                    } else if (roomClass && obj instanceof roomClass) {
                        logLine = 'Room: ' + roomDisplayName(obj) + ' - ';
                    } else if (playerClass && obj instanceof playerClass) {
                        logLine = 'Player - ';
                    } else {
                        logLine = '';
                    }
                    logLine += selectiontext;
                    const mainText = $dom("#MainText");
                    const mainTextEl = mainText.get(0);
                    if (!mainTextEl) return;
                    
                    const isEmpty = mainTextEl.innerHTML.trim() === '';
                    const scrollHeightBefore = mainTextEl.scrollHeight;
                    if (ENGINE_LOGGING_ENABLED) {
                        console.log('[GameUI.addActionChoice] Appending action:', selectionchoice, 'isEmpty:', isEmpty, 'scrollHeightBefore:', scrollHeightBefore);
                    }
                    
                    // Add <hr> before action name to separate turns (unless it's the very first action)
                    // Reuse the separator if the previous turn already ended with one
                    const reuseSeparator = !isEmpty && mainTextEndsWithTurnSeparator(mainTextEl);
                    let actionText = '';
                    if (!isEmpty && !reuseSeparator) {
                        // Not empty - this is a new turn, add <hr>
                        actionText = '<hr class="turn-separator">';
                    }
                    // Action name with two <br> after it (proper HTML: <br> not </br>)
                    actionText += (reuseSeparator ? '' : '<br>') + '<b>' + escapeHtmlSpecialCharacters(logLine) + "</b><br><br>";
                    
                    const processedText = processTextForOutput(actionText, isEmpty);
                    mainText.append(processedText);
                    
                    scrollMainTextToTurnStart({ defer: true });
                    $dom("#selectionmenu").css("visibility", "hidden");
                    ResetLoopObjects();
                    if (TheGame) TheGame.TurnCount++;
                    await GameActions.processActionAsync(selectionchoice, false, obj);
                });

                GameUI.onInteractionResume();
            }
        });

        $dom("#Actionchoices").append(div);
        return div;
    },

    displayActions: function (obj: any, clickEvent: MouseEvent): void {
        const actions = obj.Actions;
        if (GetActionCount(actions) === 0) {
            return;
        }

        $dom("#Actionchoices").empty();
        Globals.curActions = actions;
        for (let i = 0; i < actions.length; i++) {
            const action = actions[i];
            if (actionShouldBeVisible(action)) {
                this.addActionChoice(obj, action, nameForAction(action));
                this.addChildActions(obj, actions, GameUI.INDENT_STR, action.name);
            }
        }

           // Remove old handlers if they exist
        if (selectionMenuClickHandler) {
            const selectionMenu = $dom("#selectionmenu").get(0);
            if (selectionMenu) {
                selectionMenu.removeEventListener('click', selectionMenuClickHandler);
            }
        }
        if (bodyClickHandler) {
            document.body.removeEventListener('click', bodyClickHandler);
        }

        // Create new click handler for selection menu (prevents clicks inside from closing)
        selectionMenuClickHandler = (e: MouseEvent) => {
            e.stopPropagation();
        };
        const selectionMenuEl = $dom("#selectionmenu").get(0);
        if (selectionMenuEl) {
            selectionMenuEl.addEventListener('click', selectionMenuClickHandler);
        }

        // Create new body click handler to close menu when clicking outside
        bodyClickHandler = (e: Event) => {
            $dom("#selectionmenu").css("visibility", "hidden");
        };
        setTimeout(() => {
            document.body.addEventListener('click', bodyClickHandler!);
        }, 0);

        let leftPosition = clickEvent.clientX;
        let topPosition = clickEvent.clientY;
        const selectionMenu = $dom("#selectionmenu");
        const rightPosition = leftPosition + selectionMenu.width();
        const bottomPosition = topPosition + selectionMenu.height();
        const winWidth = windowWidth();
        const winHeight = windowHeight();
        const fudgeFactor = 2;
        if (rightPosition > winWidth - fudgeFactor) {
            leftPosition -= (rightPosition - winWidth + fudgeFactor);
        }
        if (bottomPosition > winHeight - fudgeFactor) {
            topPosition -= (bottomPosition - winHeight + fudgeFactor);
        }

        selectionMenu.css("top", topPosition + "px");
        selectionMenu.css("left", leftPosition + "px");
        selectionMenu.css("visibility", "visible");
        const actionChoices = $dom("#Actionchoices").get(0) as HTMLElement;
        if (actionChoices) actionChoices.focus();
    },

    addChildActions: function (obj: any, actions: any[], Indent: string, ActionName: string): void {
        for (let i = 0; i < actions.length; i++) {
            const action = actions[i];
            if (action.name.substring(0, 2) != "<<" && action.bActive && action.actionparent == ActionName) {
                this.addActionChoice(obj, action, Indent + nameForAction(action));
                this.addChildActions(obj, actions, Indent + GameUI.INDENT_STR, action.name);
            }
        }
    },

    addOpenedObjects: function (outerObject: any, thelistbox: any, itemclass: string): void {
        if (!TheGame) return;
        TheGame.Objects.forEach((innerObject: any) => {
            const inContainer = objectContainsObject(outerObject, innerObject);
            const isCharacter = outerObject instanceof Character;
            const inCharacter = isCharacter && characterHasObject(outerObject, innerObject);
            if (
                inContainer ||
                inCharacter
            ) {
                const link = GameUI.panelLink(
                    itemclass,
                    GameUI.INDENT_STR + objecttostring(innerObject),
                    innerObject.UniqueIdentifier,
                    innerObject.Actions,
                    Finder.object
                );
                if (thelistbox && thelistbox.append) {
                    thelistbox.append(link);
                } else {
                    $dom(thelistbox).append(link);
                }

                if (innerObject.bOpenable && innerObject.bOpen) {
                    GameUI.addOpenedObjects(innerObject, thelistbox, itemclass);
                }
            }
        });
    },

    refreshRoomObjects: function (): void {
        $dom("#RoomObjects").empty();
        Interactables.visibleRoomObjects().forEach((obj) => {
            $dom("#RoomObjects").append(
                GameUI.panelLink(
                    'RoomObjects',
                    objecttostring(obj),
                    obj.UniqueIdentifier,
                    obj.Actions,
                    Finder.object
                )
            );

            if (obj.bContainer) {
                if (!obj.bOpenable || obj.bOpen) {
                    GameUI.addOpenedObjects(obj, $dom("#RoomObjects"), 'RoomObjects');
                }
            }
        });
        if (TheGame && TheGame.Player.CurrentRoom != null) {
            const currentroom = Finder.room(TheGame.Player.CurrentRoom);
            if (currentroom != null) {
                for (let j = 0; j < currentroom.Exits.length; j++) {
                    const tempexit = currentroom.Exits[j];
                    if (tempexit.PortalObjectName != "<None>") {
                        const tempobj = Finder.object(tempexit.PortalObjectName);
                        if (tempobj != null) {
                            if (tempobj.bVisible) {
                                $dom("#RoomObjects").append(
                                    GameUI.panelLink(
                                        'RoomObjects',
                                        objecttostring(tempobj),
                                        tempobj.UniqueIdentifier,
                                        tempobj.Actions,
                                        Finder.object
                                    )
                                );

                                if (tempobj.bContainer) {
                                    if (!tempobj.bOpenable || tempobj.bOpen) {
                                        GameUI.addOpenedObjects(tempobj, $dom("#RoomObjects"), 'RoomObjects');
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    },

    refreshInventory: function (): void {
        $dom("#Inventory").empty();
        Interactables.visibleInventoryObjects().forEach((obj) => {
            $dom("#Inventory").append(
                GameUI.panelLink(
                    'RoomObjects',
                    objecttostring(obj),
                    obj.UniqueIdentifier,
                    obj.Actions,
                    Finder.object
                )
            );

            if (obj.bContainer) {
                if (!obj.bOpenable || obj.bOpen) {
                    GameUI.addOpenedObjects(obj, $dom("#Inventory"), 'RoomObjects');
                }
            }
        });
    },

    refreshCharacters: function (): void {
        $dom("#VisibleCharacters").empty();
        Interactables.characters().forEach((obj) => {
            $dom("#VisibleCharacters").append(
                GameUI.panelLink(
                    'VisibleCharacters',
                    CharToString(obj),
                    obj.Charname,
                    obj.Actions,
                    Finder.character
                )
            );

            if (obj.bAllowInventoryInteraction) {
                GameUI.addOpenedObjects(obj, $dom("#VisibleCharacters"), 'VisibleCharacters');
            }
        });
    },

    panelLink: function (itemClass: string, text: string, value: string, actions: any[], objFinderFunction: (val: string) => any): HTMLElement {
        const div = createElement<HTMLDivElement>('div', {
            class: itemClass,
            value: value
        }, text);
        $dom(div).toggleClass('no-actions', GetActionCount(actions) === 0);

        div.addEventListener('click', (clickEvent: MouseEvent) => {
            const selectedObj = objFinderFunction(div.getAttribute('value') || '');
            if (selectedObj != null) {
                Globals.theObj = selectedObj;
                GameUI.displayActions(selectedObj, clickEvent);
            }
        });
        return div;
    },

    refreshPanelItems: function (): void {
        this.refreshInventory();
        this.refreshRoomObjects();
        this.refreshCharacters();
    },

    displayLiveTimers: function (): void {
        // Ensure all active live timers are scheduled
        GameTimers.rescheduleAllLiveTimers();
        
        const activeLiveTimers = GameTimers.activeLiveTimers();
        const displayElement = $dom('.live-timer-display');
        const displayEl = displayElement.get(0) as HTMLElement | undefined;
        
        if (!displayEl) return;
        
        displayElement.toggle(activeLiveTimers.length > 0);
        
        if (activeLiveTimers.length > 0) {
            const container = $dom('.live-timer-display-rows');
            const tableElement = displayElement.find('table');
            const expandedContent = displayElement.find('.live-timer-expanded-content');
            const iconButton = displayElement.find('.live-timer-icon-button');
            
            // Create icon button if it doesn't exist
            if (!iconButton.get(0)) {
                const iconBtn = createElement<HTMLDivElement>('div', { class: 'live-timer-icon-button' });
                iconBtn.innerHTML = '⏱';
                iconBtn.setAttribute('title', 'Live Timers - Click to expand');
                displayEl.appendChild(iconBtn);
                
                // Set up click handler on icon button to toggle expanded state
                $dom(iconBtn).on('click', () => {
                    displayEl.classList.add('expanded');
                });
            }
            
            // Create expanded content wrapper if it doesn't exist
            if (!expandedContent.get(0)) {
                const tableEl = tableElement.get(0) as HTMLElement | undefined;
                const allH4s = displayElement.find('h4');
                
                // Remove any existing H4 elements that are direct children (from HTML)
                const h4El = allH4s.get(0) as HTMLElement | undefined;
                if (h4El && h4El.parentElement === displayEl) {
                    h4El.remove();
                }
                
                if (tableEl) {
                    const expandedWrapper = createElement<HTMLDivElement>('div', { class: 'live-timer-expanded-content' });
                    const h4Element = createElement<HTMLHeadingElement>('h4', {}, 'Live Timers');
                    
                    // Move the table into the expanded wrapper
                    const tableParent = tableEl.parentElement;
                    if (tableParent) {
                        expandedWrapper.appendChild(h4Element);
                        // Remove table from its current parent and add to wrapper
                        tableParent.removeChild(tableEl);
                        expandedWrapper.appendChild(tableEl);
                        displayEl.appendChild(expandedWrapper);
                        
                        // Set up click handler on h4 header to collapse
                        $dom(h4Element).on('click', () => {
                            displayEl.classList.remove('expanded');
                        });
                    }
                }
                
                // Only set collapsed state on initial setup
                displayEl.classList.remove('expanded');
                displayEl.setAttribute('data-initialized', 'true');
            }
            // Don't touch the expanded state after initial setup - preserve user's choice
            
            // Get existing timer rows from DOM
            const containerEl = container.get(0) as HTMLElement | undefined;
            const existingRows = Array.from(containerEl?.children || []) as HTMLTableRowElement[];
            const existingTimerMap = new Map<string, HTMLTableRowElement>();
            existingRows.forEach(row => {
                const timerName = $dom(row).data('timer-name') as string;
                if (timerName) {
                    existingTimerMap.set(timerName, row);
                }
            });
            
            // Track which timers are currently active
            const activeTimerNames = new Set(activeLiveTimers.map(t => t.Name));
            
            // Remove rows for timers that are no longer active
            existingTimerMap.forEach((row, timerName) => {
                if (!activeTimerNames.has(timerName)) {
                    row.remove();
                }
            });
            
            // Update existing rows or create new ones
            activeLiveTimers.forEach((timer) => {
                let timerRow = existingTimerMap.get(timer.Name);
                
                if (!timerRow) {
                    // Create new row for new timer
                    timerRow = createElement<HTMLTableRowElement>('tr');
                    $dom(timerRow).data('timer-name', timer.Name);
                    
                    const td1 = createElement<HTMLTableCellElement>('td', {}, timer.Name);
                    
                    const td2 = createElement<HTMLTableCellElement>('td');
                    td2.setAttribute('data-timer-countdown', '');
                    
                    // Create action buttons cell
                    const td3 = createElement<HTMLTableCellElement>('td');
                    td3.style.cssText = 'white-space: nowrap;';
                    
                    const skipBtn = createElement<HTMLButtonElement>('button', { class: 'timer-action-btn timer-skip-btn' });
                    skipBtn.textContent = 'Skip';
                    skipBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        GameTimers.skipLiveTimer(timer.Name);
                        GameUI.displayLiveTimers();
                    });
                    
                    const cancelBtn = createElement<HTMLButtonElement>('button', { class: 'timer-action-btn timer-cancel-btn' });
                    cancelBtn.textContent = 'Cancel';
                    cancelBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        GameTimers.cancelLiveTimer(timer.Name);
                        timer.Active = false;
                        timer.curtickcount = 0;
                        GameUI.refreshPanelItems();
                        GameUI.displayLiveTimers();
                    });
                    
                    td3.appendChild(skipBtn);
                    td3.appendChild(cancelBtn);
                    
                    timerRow.appendChild(td1);
                    timerRow.appendChild(td2);
                    timerRow.appendChild(td3);
                    
                    container.append(timerRow);
                }
                
                // Update countdown text for existing or new row
                const countdownCell = timerRow.querySelector('[data-timer-countdown]') as HTMLTableCellElement;
                if (countdownCell) {
                    const remainingMs = GameTimers.getLiveTimerRemaining(timer);
                    const secondsRemaining = Math.max(0, Math.ceil(remainingMs / 1000));
                    const displayTime = secondsRemaining > 0 ? String(secondsRemaining) + 's' : '0s';
                    countdownCell.textContent = displayTime;
                }
            });
        } else {
            // Remove expanded class when no timers
            displayEl.classList.remove('expanded');
            // Clear all rows when no timers
            const container = $dom('.live-timer-display-rows');
            container.empty();
        }
    },

    onInteractionResume: function (noLine?: boolean): void {
        const MainText = $dom("#MainText");
        const mainTextEl = MainText.get(0);
        if (!mainTextEl) return;
        
        scrollMainTextToTurnStart({ defer: true });

        if (gameController.shouldRunCommands() && !!!noLine && !mainTextEndsWithTurnSeparator(mainTextEl)) {
            MainText.append('<hr class="turn-separator">');
        }

        UpdateStatusBars();

        if (Settings.debugEnabled) {
            const barText = '\u2015\u2015\u2015\u2015\u2015';  // HORIZONTAL BAR x5
            console.debug('%c' + barText + ' User Interaction ' + barText,
                    'font-weight: bold; font-size: larger');
        }

        if (gameController.shouldRunCommands()) {
            GameHistory.pushState();
            $dom('#back').prop('disabled', !GameHistory.canGoBack())
                .prop('title', GameHistory.noGoBackReason());
        }
    },

    showMessage: function (text: string, params?: { type?: string; timeout?: number; html?: boolean }): void {
        if (params === undefined) {
            params = {};
        }

        const message = createElement<HTMLDivElement>('div', { class: 'message' });
        const closeBtn = createElement<HTMLDivElement>('div', { class: 'message-close' });
        message.appendChild(closeBtn);

        switch (params.type) {
            case 'error': $dom(message).addClass('error'); break;
            case 'warning': $dom(message).addClass('warning'); break;
            case 'success': $dom(message).addClass('success'); break;
            case undefined: break;
            default: console.warn(`Message type ${params.type} is not supported`);
        }

        const textDiv = createElement<HTMLDivElement>('div', { class: 'message-text' });
        if (params.html) {
            textDiv.innerHTML = text;
        } else {
            textDiv.textContent = text;
        }
        if (ENGINE_LOGGING_ENABLED) {
            console.log(text);
        }

        message.insertBefore(textDiv, closeBtn);

        message.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('.message-close')) {
                message.remove();
            }
        });

        $dom("#MessagePane").append(message);

        if (params.timeout !== undefined) {
            window.setTimeout(() => {
                message.remove();
            }, params.timeout * 1000.0);
        }
    },

    /** Convert the given color to use in dark mode.
     * The color is converted to HSL and the lightness component is inverted to
     * produce the dark mode color.
     */
    getDarkColor: function (color: string): string {
        color = color.trim();

        // Use value from cache if available
        let result = this.darkColorMap[color];
        if (result !== undefined) return String(result);

        // Convert CSS color to rgb(a) color
        if (this.darkColorElement === null) {
            this.darkColorElement = document.createElement('div');
            this.darkColorElement.id = 'dark-color-element';
            this.darkColorElement.style.display = 'none';
            document.body.appendChild(this.darkColorElement);
        }

        this.darkColorElement.style.color = color;
        const rgbColor = window.getComputedStyle(this.darkColorElement).color;

        // Parse the color
        const m = rgbColor.match(/^rgb(a)?\s*\(\s*([^\)]+)\s*\)/);
        if (!m) {
            console.warn('Cannot parse color "' + color + '" (' + rgbColor + ')');
            return color;
        }

        const hasAlpha = m[1] !== undefined;
        const rgba = m[2].split(',').map(comp => parseFloat(comp));

        if (rgba.length < 3) {
            console.warn('Cannot parse color "' + color + '" (' + rgbColor + ')');
            return color;
        }

        const rgb = rgba.slice(0, 3);

        // Use value from cache if available
        let hsl = this.darkColorMap[rgb.join(',')] as number[];
        if (hsl === undefined) {
            // Convert to HSL
            // https://css-tricks.com/converting-color-spaces-in-javascript/
            function RGBToHSL(r: number, g: number, b: number): number[] {
                // Make r, g, and b fractions of 1
                r /= 255;
                g /= 255;
                b /= 255;

                // Find greatest and smallest channel values
                const cmin = Math.min(r, g, b);
                const cmax = Math.max(r, g, b);
                const delta = cmax - cmin;
                let h = 0, s = 0, l = 0;

                // Calculate hue
                // No difference
                if (delta == 0) h = 0;
                // Red is max
                else if (cmax == r) h = ((g - b) / delta) % 6;
                // Green is max
                else if (cmax == g) h = (b - r) / delta + 2;
                // Blue is max
                else h = (r - g) / delta + 4;

                h = Math.round(h * 60);

                // Make negative hues positive behind 360°
                if (h < 0) h += 360;

                // Calculate lightness
                l = (cmax + cmin) / 2;

                // Calculate saturation
                s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

                // Multiply l and s by 100
                s = +(s * 100).toFixed(1);
                l = +(l * 100).toFixed(1);

                return [h, s, l];
            }

            hsl = RGBToHSL(rgb[0], rgb[1], rgb[2]);
            // Invert the lightness
            hsl[2] = 100 - hsl[2] * 0.65;

            // Cache the conversion from rgb
            this.darkColorMap[rgb.join(',')] = hsl;
        }

        result = 'hsl(' + hsl[0] + ' ' + hsl[1] + '% ' + hsl[2] + '%' +
            (hasAlpha ? (' / ' + rgba[3]) : '') + ')';

        // Cache the conversion from color text
        this.darkColorMap[color] = result;

        return result;
    },

    setDarkMode: function (enabled: boolean): void {
        if (enabled) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }

        // Convert the colors in MainText
        document.querySelectorAll('#MainText span[style^="color:"]').forEach((el) => {
            const htmlEl = el as HTMLElement;
            if (enabled) {
                if (htmlEl.dataset.color === undefined) {
                    htmlEl.dataset.color = htmlEl.style.color;
                    htmlEl.style.color = this.getDarkColor(htmlEl.style.color);
                }
            } else {
                if (htmlEl.dataset.color !== undefined) {
                    htmlEl.style.color = htmlEl.dataset.color;
                    delete htmlEl.dataset.color;
                }
            }
        });
    },

    playBgMusic: function (path: string | null): void {
        const mplayer = $dom("#BGMusic").get(0) as HTMLAudioElement;
        if (!mplayer) return;
        if (path === null) {
            // Stop
            mplayer.pause();
        } else {
            let volume = 0;
            mplayer.volume = 0;

            $dom("#bgmusicsource").attr("src", path);
            mplayer.load();
            mplayer.play();

            // Fade-in effect
            clearInterval(this.bgMusicTimer);
            this.bgMusicTimer = window.setInterval(() => {
                if (volume >= (Settings as any).musicVolume) {
                    clearInterval(this.bgMusicTimer);
                } else {
                    ++volume;
                    mplayer.volume = volume / 100;
                }
            }, 15);
        }
    },

    playSoundEffect: function (path: string): void {
        const mplayer = $dom("#SoundEffect").get(0) as HTMLAudioElement;
        if (!mplayer) return;
        if (path === null) {
            // Stop (unused)
            mplayer.pause();
        } else {
            mplayer.src = path;
            mplayer.load();
            mplayer.play();
        }
    },
};

// Make available globally
(window as any).GameUI = GameUI;
